import { inject } from 'tsyringe';
import { IRemoteObject } from './remote-object.interface';
import { AsyncLock } from 'app-core/src/utils/async-lock';
import { LoggerService } from '../logger.service';
import { ITimerServiceDelegate, TimerService } from '../timer.service';
import { RemoteObjectRetrieverServiceOptions } from '../../interfaces/configurations.interface';

export const kDITRemoteObjectRetrieverServiceOptions =
  'RemoteObjectRetrieverServiceOptions';

// -----------------------------------------------------------------------------
// Use this class to retrieve a remote object (https, ftp etc...) and
// periodically check if it was updated
//
// eslint-disable-next-line max-len
// TODO(twolf): Implement a persistent cache so that we can use it as a fallback in case we failed getting it from the server
// TODO(twolf): Write tests
export class RemoteObjectRetrieverService<T> implements ITimerServiceDelegate {
  private readonly logger = LoggerService.getCategory(
    'RemoteObjectRetrieverService',
  );

  private latestRetrievedObject: T = undefined;

  // NOTE(twolf): We use this to refrain from an endless retry (e.g. when the
  // user has no network or something is wrong with our servers)
  private preformedFirstRetrievalAttempt = false;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITRemoteObjectRetrieverServiceOptions)
    public options: RemoteObjectRetrieverServiceOptions,
    private readonly remoteObject: IRemoteObject,
    private readonly timerService: TimerService,
    private readonly singleRequestLock: AsyncLock,
  ) {
    remoteObject.initialize(options.configUrl);

    timerService.initialize({
      intervalInMs: options.retrievalIntervalInMs,
      autoReset: true,
    });

    timerService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // We want to implement lazy-loading - so we only actually fetch data from the
  // server upon the first request. We will also start the timer at that point.
  public async getObject(): Promise<T> {
    await this.singleRequestLock.lock();

    try {
      if (this.preformedFirstRetrievalAttempt) {
        return this.latestRetrievedObject;
      }

      await this.tryToRetrieveRemoteObjectWithRetries(this.options.maxRetries);
      this.timerService.start();
      if (!this.latestRetrievedObject) {
        throw new Error('Failed to retrieve config');
      }

      return this.latestRetrievedObject;
    } finally {
      this.preformedFirstRetrievalAttempt = true;
      await this.singleRequestLock.unlock();
    }
  }

  // ---------------------------------------------------------------------------
  public async initConfigUrl(url: string) {
    this.remoteObject.initialize(url);
    await this.tryToRetrieveRemoteObject();
  }

  // ---------------------------------------------------------------------------
  private async tryToRetrieveRemoteObjectWithRetries(
    retries: number,
  ): Promise<boolean> {
    let succeeded = false;

    while (!succeeded && retries > 0) {
      succeeded = await this.handleSingleRetrievalRetry();
      retries--;
    }

    this.preformedFirstRetrievalAttempt = true;

    if (succeeded) {
      return true;
    }

    this.logger.error('Failed all retries!');
    return false;
  }

  // ---------------------------------------------------------------------------
  private async handleSingleRetrievalRetry(): Promise<boolean> {
    try {
      await this.tryToRetrieveRemoteObject();
      return true;
    } catch (err) {
      await this.timerService.sleep(
        this.options.waitIntervalBetweenRetriesInMs,
      );
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  private async tryToRetrieveRemoteObject() {
    try {
      const [object, lastModified] = await this.remoteObject.retrieve<T>();

      this.latestRetrievedObject = object;
      this.logger.info(`Retrieved remote config ${lastModified}`);
    } catch (err) {
      this.logger.error('Failed to retrieve remote object', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // TimerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onTimer() {
    this.logger.info('Try to retrieve updated remote object ...');
    await this.tryToRetrieveRemoteObjectWithRetries(this.options.maxRetries);
  }
}
